import { createFileRoute } from '@tanstack/react-router';
import * as React from 'react';

import { PageHeader } from '@/components/dashboard/page-header';
import { DataTable, TableTag, type DataTableColumn } from '@/components/data-table';
import { trpc } from '@/lib/trpc/client';

export const Route = createFileRoute('/_admin/admin/overrides')({
  component: OverridesPage,
});

const ROLES = ['REQUESTER', 'BIDDER'] as const;
type RoleValue = (typeof ROLES)[number];

interface OverrideRow {
  id: string;
  userId: string;
  role: string;
  rate: { toString: () => string };
  startsAt: Date;
  endsAt: Date | null;
  reason: string;
  grantedById: string | null;
  createdAt: Date;
  user: { email: string; displayName: string | null };
}

function OverridesPage(): React.ReactElement {
  const utils = trpc.useUtils();
  const list = trpc.admin.overrideList.useQuery({ limit: 50 });
  const rows = (list.data ?? []) as OverrideRow[];

  const [form, setForm] = React.useState({
    userId: '',
    role: 'REQUESTER' as RoleValue,
    rate: '',
    startsAt: '',
    endsAt: '',
    reason: '',
  });
  const [showForm, setShowForm] = React.useState(false);

  const create = trpc.admin.createCommissionOverride.useMutation({
    onSuccess: () => {
      setShowForm(false);
      setForm({ userId: '', role: 'REQUESTER', rate: '', startsAt: '', endsAt: '', reason: '' });
      void utils.admin.overrideList.invalidate();
    },
  });

  const del = trpc.admin.deleteCommissionOverride.useMutation({
    onSuccess: () => {
      void utils.admin.overrideList.invalidate();
    },
  });

  const columns: DataTableColumn<OverrideRow>[] = [
    {
      key: 'user',
      header: 'Compte',
      cell: (row) => <span className="font-medium">{row.user.email}</span>,
    },
    { key: 'role', header: 'Rôle', cell: (row) => <TableTag label={row.role} /> },
    {
      key: 'rate',
      header: 'Taux',
      align: 'right',
      cell: (row) => (
        <span className="font-mono tabular-nums">
          {(Number(row.rate.toString()) * 100).toFixed(1)} %
        </span>
      ),
    },
    {
      key: 'period',
      header: 'Période',
      cell: (row) => (
        <span className="text-muted-foreground text-xs">
          {new Date(row.startsAt).toLocaleDateString('fr-FR')}
          {row.endsAt ? ` → ${new Date(row.endsAt).toLocaleDateString('fr-FR')}` : ' → ∞'}
        </span>
      ),
    },
    {
      key: 'reason',
      header: 'Motif',
      cell: (row) => (
        <span className="text-muted-foreground line-clamp-1 max-w-xs text-xs">{row.reason}</span>
      ),
    },
  ];

  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-6">
      <PageHeader
        title="Overrides commission"
        subtitle="Taux de commission personnalisés par utilisateur."
      />
      <div className="space-y-5">
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => setShowForm((v) => !v)}
            className="bg-primary text-primary-foreground rounded-lg px-4 py-2 text-sm font-medium"
          >
            {showForm ? 'Annuler' : 'Créer un override'}
          </button>
        </div>
        {showForm ? (
          <div className="bg-card border-border space-y-3 rounded-xl border p-5">
            <p className="text-muted-foreground text-xs">Requester: 0–10% | Bidder: 0–5%</p>
            <div className="grid gap-3 sm:grid-cols-2">
              <input
                aria-label="User ID"
                className="border-border bg-background rounded-lg border px-3 py-1.5 text-sm"
                placeholder="User ID"
                value={form.userId}
                onChange={(e) => setForm((p) => ({ ...p, userId: e.target.value }))}
              />
              <select
                aria-label="Rôle"
                className="border-border bg-background rounded-lg border px-3 py-1.5 text-sm"
                value={form.role}
                onChange={(e) => setForm((p) => ({ ...p, role: e.target.value as RoleValue }))}
              >
                {ROLES.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
              <input
                aria-label="Taux de commission"
                className="border-border bg-background rounded-lg border px-3 py-1.5 text-sm"
                placeholder="Taux (ex: 0.03)"
                value={form.rate}
                onChange={(e) => setForm((p) => ({ ...p, rate: e.target.value }))}
              />
              <input
                aria-label="Début"
                type="datetime-local"
                className="border-border bg-background rounded-lg border px-3 py-1.5 text-sm"
                value={form.startsAt}
                onChange={(e) => setForm((p) => ({ ...p, startsAt: e.target.value }))}
              />
              <input
                aria-label="Fin"
                type="datetime-local"
                className="border-border bg-background rounded-lg border px-3 py-1.5 text-sm"
                value={form.endsAt}
                onChange={(e) => setForm((p) => ({ ...p, endsAt: e.target.value }))}
              />
              <input
                aria-label="Motif"
                className="border-border bg-background rounded-lg border px-3 py-1.5 text-sm"
                placeholder="Motif…"
                value={form.reason}
                onChange={(e) => setForm((p) => ({ ...p, reason: e.target.value }))}
              />
            </div>
            {create.error ? (
              <p className="text-destructive text-xs">{create.error.message}</p>
            ) : null}
            <button
              type="button"
              disabled={
                create.isPending || !form.userId || !form.rate || !form.startsAt || !form.reason
              }
              onClick={() =>
                create.mutate({
                  userId: form.userId,
                  role: form.role,
                  rate: parseFloat(form.rate),
                  startsAt: new Date(form.startsAt).toISOString(),
                  endsAt: form.endsAt ? new Date(form.endsAt).toISOString() : undefined,
                  reason: form.reason,
                })
              }
              className="bg-primary text-primary-foreground rounded-lg px-4 py-1.5 text-sm font-medium disabled:opacity-50"
            >
              {create.isPending ? 'Création…' : 'Créer'}
            </button>
          </div>
        ) : null}
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(row) => row.id}
          actionHeader=""
          rowAction={(row) => (
            <button
              type="button"
              aria-label={`Supprimer l'override de ${row.user.email}`}
              disabled={del.isPending}
              onClick={() => del.mutate({ overrideId: row.id })}
              className="text-destructive border-destructive/30 hover:bg-destructive/5 rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-50"
            >
              {del.isPending ? 'Suppression…' : 'Supprimer'}
            </button>
          )}
          emptyState={<p className="text-muted-foreground text-center text-sm">Aucun override.</p>}
        />
      </div>
    </div>
  );
}
